import { styled } from "@mui/material";
import {
  useSignatureDispatchProvider,
  useSignatureProvider,
} from "pages/signatures/providers/manage-signature/SignatureProvider";
import React from "react";
import { fontSize, gaps } from "theme/defaultStyle";
import { getImageTypeFromImageDataUrl } from "utils/signature/imageFile";
import TypedFont from "./TypedFont";

type Fonts =
  | "Freestyle Script"
  | "Palace Script MT"
  | "Brush Script MT"
  | "Pristina"
  | "Alex Brush"
  | "Lucida Handwriting";

export type FontConfig = {
  font: Fonts;
  style: string;
  weight: string;
  id: string;
  fontSize: number;
  topMargin: number;
  leftMargin: number;
};

const fontFamilies: FontConfig[] = [
  {
    font: "Freestyle Script",
    style: "",
    weight: "",
    id: "freestyle",
    fontSize: 38,
    topMargin: 0,
    leftMargin: 0,
  },
  {
    font: "Palace Script MT",
    style: "",
    weight: "",
    id: "palace",
    fontSize: 40,
    topMargin: 0,
    leftMargin: 0,
  },
  {
    font: "Brush Script MT",
    style: "italic",
    weight: "",
    id: "brush",
    fontSize: 36,
    topMargin: 0,
    leftMargin: 0,
  },
  {
    font: "Pristina",
    style: "italic",
    weight: "",
    id: "pristina",
    fontSize: 26,
    topMargin: 0,
    leftMargin: 0,
  },
  {
    font: "Alex Brush",
    style: "italic",
    weight: "",
    id: "alex",
    fontSize: 36,
    topMargin: 0,
    leftMargin: 0,
  },
  {
    font: "Lucida Handwriting",
    style: "",
    weight: "",
    id: "lucida",
    fontSize: 30,
    topMargin: 0,
    leftMargin: 0,
  },
];

const FontPickerContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: gaps.small1,
  "& > .MuiTypography-root": {
    fontSize: fontSize.large,
  },
  "& > div": {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    rowGap: gaps.medium1,
  },
});

interface TypeSignatureProps {
  className?: string;
}

export default function TypeSignature({ className }: TypeSignatureProps) {
  const {
    state: { signature },
  } = useSignatureProvider();
  const { dispatch } = useSignatureDispatchProvider();

  const handleLoadCompleted = (
    image: string,
    fontFamilyID: string,
    imageType: string
  ) => {
    dispatch({
      type: "FILE_UPDATE",
      signatureFile: image,
      fontFamilyID: fontFamilyID,
      imageType: imageType,
    });
  };

  const handleOnSelectSignature = (fontFamilyID: string) => {
    const container = document.getElementById(fontFamilyID);
    if (!container) return;
    const canvas: HTMLCanvasElement = document.getElementById(
      `${fontFamilyID}_canvas`
    ) as HTMLCanvasElement;
    if (!canvas) return;

    const imageData = canvas.toDataURL();
    const imageType = getImageTypeFromImageDataUrl(imageData);
    handleLoadCompleted(imageData, fontFamilyID, imageType);
  };

  return (
    <FontPickerContainer className={className}>
      <div>
        {fontFamilies.map((fontFamily) => (
          <TypedFont
            key={fontFamily.id}
            fontConfig={fontFamily}
            signature={signature}
            handleSelectedSignature={handleOnSelectSignature}
          />
        ))}
      </div>
    </FontPickerContainer>
  );
}
